import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { CheckCircle2, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <Card className="w-full max-w-md shadow-xl text-center p-8">
          <p className="text-gray-600 mb-4">This reset link is invalid or missing.</p>
          <Link to="/forgot-password" className="text-[#e74c3c] hover:underline text-sm">Request a new one</Link>
        </Card>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: newPassword });
      setDone(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center">
          <img
            src="https://customer-assets.emergentagent.com/job_20414c3a-9623-4d12-8f24-8780a5aa64c8/artifacts/y936iv32_%3D2_transparent_logo.png"
            alt="Equaltoo"
            className="h-20 w-auto mx-auto mb-4"
          />
          {done ? (
            <>
              <div className="flex justify-center mb-2">
                <CheckCircle2 className="w-12 h-12 text-green-500" />
              </div>
              <CardTitle className="text-2xl text-gray-900">Password updated!</CardTitle>
              <CardDescription className="text-base mt-1">
                You'll be redirected to login in a moment.
              </CardDescription>
            </>
          ) : (
            <>
              <CardTitle className="text-2xl text-gray-900">Set a new password</CardTitle>
              <CardDescription className="text-base mt-1">
                Choose a strong password of at least 6 characters.
              </CardDescription>
            </>
          )}
        </CardHeader>

        {!done && (
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="new-password">New Password</Label>
                <div className="relative mt-2">
                  <Input
                    id="new-password"
                    type={showPw ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    placeholder="At least 6 characters"
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(!showPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="confirm-password">Confirm Password</Label>
                <Input
                  id="confirm-password"
                  type={showPw ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  placeholder="Repeat your new password"
                  className="mt-2"
                  data-testid="confirm-password-input"
                />
              </div>

              {error && (
                <p className="text-sm text-red-600" data-testid="reset-error">{error}</p>
              )}

              <Button
                type="submit"
                className="w-full bg-[#e74c3c] hover:bg-[#c0392b]"
                size="lg"
                disabled={loading}
                data-testid="reset-submit-btn"
              >
                {loading ? 'Updating…' : 'Update Password'}
              </Button>
            </form>
          </CardContent>
        )}

        {!done && (
          <div className="px-6 pb-6 text-center">
            <Link
              to="/login"
              className="inline-flex items-center gap-1.5 text-sm text-[#666] hover:text-[#003366] transition-colors"
            >
              <ArrowLeft className="w-4 h-4" /> Back to login
            </Link>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ResetPassword;
